import { useQuery } from "@tanstack/vue-query";
import type { MaybeRef } from "vue";
import { computed, unref } from "vue";
import { customInstance } from "@/custom-axios";
import type { ErrorType } from "@/custom-axios";

export interface EmailDeliveryTracking {
  id: number;
  dispatchedReportId: number;
  recipientEmail: string;
  status: string;
  errorDetails: Record<string, unknown> | null;
  sentAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedEmailDeliveryTrackingsList {
  items: EmailDeliveryTracking[];
  currentPage: number;
  perPage: number;
  totalPages: number;
  totalResults: number;
}

interface GetEmailDeliveryTrackingsParams {
  automatedReportId: number | string;
  page: number;
  perPage?: number;
}

const getEmailDeliveryTrackings = async (
  params: GetEmailDeliveryTrackingsParams,
): Promise<PaginatedEmailDeliveryTrackingsList> => {
  const response = await customInstance<{ data: PaginatedEmailDeliveryTrackingsList }>(
    "/api/frontend/automatedReports/emailDeliveryTrackings",
    {
      method: "GET",
      params: {
        automatedReportId: params.automatedReportId,
        page: params.page,
        perPage: params.perPage ?? 10,
      },
    },
  );

  return response.data;
};

export const getEmailDeliveryTrackingsQueryKey = (
  automatedReportId: MaybeRef<number | string>,
  page: MaybeRef<number>,
) => ["emailDeliveryTrackings", automatedReportId, page] as const;

export function useGetEmailDeliveryTrackingsQuery(
  automatedReportId: MaybeRef<number | string>,
  page: MaybeRef<number>,
  perPage: number = 10,
) {
  const queryKey = computed(() =>
    getEmailDeliveryTrackingsQueryKey(unref(automatedReportId), unref(page)),
  );

  return useQuery<PaginatedEmailDeliveryTrackingsList, ErrorType<unknown>>({
    queryKey,
    queryFn: () =>
      getEmailDeliveryTrackings({
        automatedReportId: unref(automatedReportId),
        page: unref(page),
        perPage,
      }),
  });
}
